import {
  formatFreeTilesLayout,
  parseFreeTilesLayout
} from 'common/buyOneGetManyLayout';
import { FreeTilesLayout, IOnePlusManyInstanceConfig } from 'common/contracts';
import {
  EBlockType,
  IOnePlusManyStoreBlock,
  TStoreBlock
} from 'common/TemplateStudio.types';
import {
  buildOnePlusManyInstanceId,
  DEFAULT_BODY_BLOCKS_ORDER,
  OnePlusManyInstanceId
} from 'constants/bodyBlocks';
import { StoreBodyBlock } from 'constants/enums';
import { processRichTextContent } from 'utils/colorUtils';
import { v4 } from 'uuid';
import type { z } from 'zod';

import { EThemes } from 'components/TemplatesStudio/enums';

import type { templateStudioSchema } from '../Components/FormPage/validation-schema';
import {
  FIRST_INSTANCE_SUFFIX,
  parseOnePlusManyInstanceKeyFromWireId
} from '../multiInstanceBlocks/multiInstanceBlocks.consts';
import type {
  IFormStoreBlock,
  IFormStoreBlockStub,
  IOnePlusManyFormStoreBlock
} from '../multiInstanceBlocks/multiInstanceBlocks.types';

type BuildDefaultOnePlusManyInstance = (
  instanceKey: OnePlusManyInstanceId
) => IOnePlusManyFormStoreBlock;

const DEFAULT_ONE_PLUS_MANY_MAX_OFFERS = 10;

type TemplateStudioFormValues = z.infer<typeof templateStudioSchema>;

type BlockFields = Record<string, unknown>;

interface BlockBuilder {
  type: EBlockType;
  build: (values: TemplateStudioFormValues) => BlockFields;
}

export const E_BLOCK_TYPE_TO_STORE_BODY_BLOCK: Partial<
  Record<EBlockType, StoreBodyBlock>
> = {
  [EBlockType.BUNDLE]: StoreBodyBlock.bundles,
  [EBlockType.PROMOTION]: StoreBodyBlock.specialOffers,
  [EBlockType.ROLLING_OFFER]: StoreBodyBlock.rollingOffers,
  [EBlockType.ONE_PLUS_MANY]: StoreBodyBlock.onePlusMany,
  [EBlockType.DAILY_BONUS]: StoreBodyBlock.dailyBonusBadge,
  [EBlockType.REWARD_CALENDAR]: StoreBodyBlock.rewardCalendar,
  [EBlockType.TRIGGERED_POPUP]: StoreBodyBlock.triggeredPopups,
  [EBlockType.PROGRESS_BAR]: StoreBodyBlock.progressBars,
  [EBlockType.RICH_TEXT]: StoreBodyBlock.richText,
  [EBlockType.BANNER]: StoreBodyBlock.banners,
  [EBlockType.PLAYER_LEVEL]: StoreBodyBlock.playerLevel
};

const STORE_BODY_BLOCK_TO_BUILDER: Partial<
  Record<StoreBodyBlock, BlockBuilder>
> = {
  [StoreBodyBlock.bundles]: {
    type: EBlockType.BUNDLE,
    build: (values) => {
      const source = values.bundle;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      if (source?.offersPerRow != null) {
        fields.offersPerRow = Number(source.offersPerRow);
      }
      assignIfDefined(fields, 'bundleViewModel', source?.bundleViewModel);
      return fields;
    }
  },
  [StoreBodyBlock.specialOffers]: {
    type: EBlockType.PROMOTION,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', values.promotion?.selectedDesignId);
      assignIfDefined(fields, 'themes', values.promotion?.themes);
      return fields;
    }
  },
  [StoreBodyBlock.rollingOffers]: {
    type: EBlockType.ROLLING_OFFER,
    build: (values) => {
      const source = values.rollingOffers;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      assignIfDefined(fields, 'desktopLayout', source?.desktopLayout);
      return fields;
    }
  },
  [StoreBodyBlock.dailyBonusBadge]: {
    type: EBlockType.DAILY_BONUS,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', values.popup?.selectedDesignId);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        values.dailyBonusBadge?.selectedDesignId
      );
      assignIfDefined(fields, 'badgeThemes', values.dailyBonusBadge?.themes);
      return fields;
    }
  },
  [StoreBodyBlock.rewardCalendar]: {
    type: EBlockType.REWARD_CALENDAR,
    build: (values) => {
      const source = values.rewardCalendar;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        source?.selectedBadgeDesignId
      );
      return fields;
    }
  },
  [StoreBodyBlock.triggeredPopups]: {
    type: EBlockType.TRIGGERED_POPUP,
    build: (values) => {
      const source = values.triggeredPopups;
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
      assignIfDefined(fields, 'themes', source?.themes);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        source?.selectedBadgeDesignId
      );
      return fields;
    }
  },
  [StoreBodyBlock.progressBars]: {
    type: EBlockType.PROGRESS_BAR,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(fields, 'selectedDesignId', values.progressBar?.selectedDesignId);
      return fields;
    }
  },
  [StoreBodyBlock.richText]: {
    type: EBlockType.RICH_TEXT,
    build: (values) => {
      const processed = processRichTextContent(values.richText);
      const fields: BlockFields = {};
      assignIfDefined(fields, 'text', processed?.text);
      assignIfDefined(fields, 'fontColor', processed?.fontColor);
      return fields;
    }
  },
  [StoreBodyBlock.banners]: {
    type: EBlockType.BANNER,
    build: () => ({})
  },
  [StoreBodyBlock.playerLevel]: {
    type: EBlockType.PLAYER_LEVEL,
    build: () => ({})
  }
};

const assignIfDefined = (
  fields: BlockFields,
  key: string,
  value: unknown
): void => {
  if (value !== undefined && value !== null) {
    fields[key] = value;
  }
};

const isOnePlusManyFormBlock = (
  block: IFormStoreBlock
): block is IOnePlusManyFormStoreBlock => block.type === EBlockType.ONE_PLUS_MANY;

export const isRemovableStoreBlock = (
  storeBlocks: IFormStoreBlock[],
  blockId: string
): boolean => {
  const target = storeBlocks.find((block) => block.id === blockId);
  if (target === undefined) return false;
  if (!isOnePlusManyFormBlock(target)) return true;
  return storeBlocks.filter(isOnePlusManyFormBlock).length > 1;
};

const coerceRewardsLayoutCount = (value: unknown): 1 | 2 | 3 => {
  if (value === 1 || value === 2 || value === 3) {
    return value;
  }
  return parseFreeTilesLayout(value as FreeTilesLayout | undefined);
};

const toOnePlusManyInstanceConfig = (
  block: IOnePlusManyStoreBlock
): IOnePlusManyInstanceConfig => {
  let maxOffersValue = DEFAULT_ONE_PLUS_MANY_MAX_OFFERS;
  if (typeof block.maxOffers.value === 'number') {
    maxOffersValue = block.maxOffers.value;
  }
  return {
    desktopLayout: block.desktopLayout,
    themes: (block.themes || []) as EThemes[],
    selectedDesignId: block.selectedDesignId || '',
    maxOffers: {
      unlimited: block.maxOffers.unlimited,
      value: maxOffersValue
    },
    rewardsLayout: coerceRewardsLayoutCount(block.rewardsLayout)
  };
};

const toOnePlusManyFormStoreBlock = (
  block: IOnePlusManyStoreBlock,
  fallbackSuffix: number
): IOnePlusManyFormStoreBlock => {
  let instanceKey = parseOnePlusManyInstanceKeyFromWireId(block.id);
  if (instanceKey === undefined) {
    instanceKey = buildOnePlusManyInstanceId(fallbackSuffix);
  }
  return {
    id: block.id,
    instanceKey,
    type: EBlockType.ONE_PLUS_MANY,
    isVisible: block.isVisible,
    mergedTo: block.mergedTo,
    ...toOnePlusManyInstanceConfig(block)
  };
};

const toFormStoreBlockStub = (block: TStoreBlock): IFormStoreBlockStub => ({
  id: block.id,
  type: block.type,
  isVisible: block.isVisible,
  mergedTo: block.mergedTo
});

const resolveStoreBodyBlockFromFormBlock = (
  block: IFormStoreBlock
): StoreBodyBlock | undefined => E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[block.type];

const ensureStoreBlockOfBodyBlock = (
  storeBlocks: IFormStoreBlock[],
  targetStoreBodyBlock: StoreBodyBlock,
  defaultIsVisible: boolean
): IFormStoreBlock[] => {
  const builder = STORE_BODY_BLOCK_TO_BUILDER[targetStoreBodyBlock];
  if (builder === undefined) return storeBlocks;

  const hasBlock = storeBlocks.some(
    (block) =>
      resolveStoreBodyBlockFromFormBlock(block) === targetStoreBodyBlock
  );
  if (hasBlock) return storeBlocks;

  const newBlock: IFormStoreBlockStub = {
    id: `bb-${String(targetStoreBodyBlock)}-${v4()}`,
    type: builder.type,
    isVisible: defaultIsVisible
  };

  const precedingBlocks = new Set(
    DEFAULT_BODY_BLOCKS_ORDER.slice(
      0,
      DEFAULT_BODY_BLOCKS_ORDER.indexOf(targetStoreBodyBlock)
    )
  );
  const insertIndex = storeBlocks.reduce((acc, block, index) => {
    const storeBodyBlock = resolveStoreBodyBlockFromFormBlock(block);
    if (storeBodyBlock !== undefined && precedingBlocks.has(storeBodyBlock)) {
      return index + 1;
    }
    return acc;
  }, 0);

  const next = [...storeBlocks];
  next.splice(insertIndex, 0, newBlock);
  return next;
};

const STORE_BODY_BLOCKS_VISIBLE_WHEN_INJECTED: ReadonlySet<StoreBodyBlock> =
  new Set([StoreBodyBlock.banners]);

export const ensureAllEnabledStoreBlocks = (
  storeBlocks: IFormStoreBlock[],
  enabledOrder: StoreBodyBlock[],
  buildDefaultOnePlusManyInstance: BuildDefaultOnePlusManyInstance
): IFormStoreBlock[] => {
  const withStubs = enabledOrder.reduce(
    (acc, storeBodyBlock) =>
      storeBodyBlock === StoreBodyBlock.onePlusMany
        ? acc
        : ensureStoreBlockOfBodyBlock(
            acc,
            storeBodyBlock,
            STORE_BODY_BLOCKS_VISIBLE_WHEN_INJECTED.has(storeBodyBlock)
          ),
    storeBlocks
  );

  const needsOnePlusMany =
    enabledOrder.includes(StoreBodyBlock.onePlusMany) &&
    !withStubs.some((block) => block.type === EBlockType.ONE_PLUS_MANY);
  if (!needsOnePlusMany) return withStubs;

  const instance = buildDefaultOnePlusManyInstance(
    buildOnePlusManyInstanceId(FIRST_INSTANCE_SUFFIX)
  );
  return [...withStubs, instance];
};

export const buildDefaultFormStoreBlocks = (
  enabledOrder: StoreBodyBlock[],
  buildDefaultOnePlusManyInstance: BuildDefaultOnePlusManyInstance
): IFormStoreBlock[] =>
  enabledOrder.flatMap<IFormStoreBlock>((storeBodyBlock) => {
    if (storeBodyBlock === StoreBodyBlock.onePlusMany) {
      return [
        buildDefaultOnePlusManyInstance(
          buildOnePlusManyInstanceId(FIRST_INSTANCE_SUFFIX)
        )
      ];
    }
    const builder = STORE_BODY_BLOCK_TO_BUILDER[storeBodyBlock];
    if (builder === undefined) return [];
    return [
      {
        id: `bb-${String(storeBodyBlock)}-${v4()}`,
        type: builder.type,
        isVisible: true
      }
    ];
  });

export const hydrateFormStoreBlocksFromWire = (
  wireStoreBlocks: TStoreBlock[] | undefined
): IFormStoreBlock[] => {
  if (!wireStoreBlocks || wireStoreBlocks.length === 0) {
    return [];
  }
  return wireStoreBlocks.map((block, index) => {
    if (block.type === EBlockType.ONE_PLUS_MANY) {
      return toOnePlusManyFormStoreBlock(
        block as IOnePlusManyStoreBlock,
        index + 1
      );
    }
    return toFormStoreBlockStub(block);
  });
};

const buildOnePlusManyWireBlock = (
  instance: IOnePlusManyFormStoreBlock
): IOnePlusManyStoreBlock => ({
  id: instance.id,
  type: EBlockType.ONE_PLUS_MANY,
  isVisible: instance.isVisible,
  mergedTo: instance.mergedTo,
  desktopLayout: instance.desktopLayout,
  themes: instance.themes,
  selectedDesignId: instance.selectedDesignId,
  maxOffers: {
    unlimited: instance.maxOffers.unlimited,
    value: instance.maxOffers.value
  },
  rewardsLayout: formatFreeTilesLayout(instance.rewardsLayout)
});

const buildWireBlockFromStub = (
  stub: IFormStoreBlockStub,
  values: TemplateStudioFormValues
): TStoreBlock | undefined => {
  const storeBodyBlock = E_BLOCK_TYPE_TO_STORE_BODY_BLOCK[stub.type];
  if (storeBodyBlock === undefined) return undefined;
  const builder = STORE_BODY_BLOCK_TO_BUILDER[storeBodyBlock];
  if (!builder) return undefined;
  return {
    id: stub.id,
    type: builder.type,
    isVisible: stub.isVisible,
    mergedTo: stub.mergedTo,
    ...builder.build(values)
  } as TStoreBlock;
};

export const buildStoreBlocksFromForm = (
  values: TemplateStudioFormValues
): TStoreBlock[] => {
  const formStoreBlocks = (values as { storeBlocks?: IFormStoreBlock[] })
    .storeBlocks;
  if (!Array.isArray(formStoreBlocks) || formStoreBlocks.length === 0) {
    return [];
  }

  const wireBlocks: TStoreBlock[] = [];
  for (const entry of formStoreBlocks) {
    if (isOnePlusManyFormBlock(entry)) {
      wireBlocks.push(buildOnePlusManyWireBlock(entry));
      continue;
    }
    const wireBlock = buildWireBlockFromStub(entry, values);
    if (wireBlock !== undefined) {
      wireBlocks.push(wireBlock);
    }
  }
  return wireBlocks;
};

export const buildPreviewStoreBlocks = (
  values: TemplateStudioFormValues
): TStoreBlock[] => buildStoreBlocksFromForm(values);
